import React, { useMemo, useRef } from 'react';
import moment from 'moment';
import _ from 'lodash-es';
import { useIntl, useParams, getLocale } from 'umi';
import { useCreation, useDebounce, useMemoizedFn, useSetState } from 'ahooks';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import { getCountryFlag } from '@/pages/home/_config/countries';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import { useRevenueFilter, useSimilar } from './hooks';
import CompaniesTable from '../Financials/components/CompaniesTable';
import { smilarCompaniesTableData } from './config';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import '@/pages/intelligence/assets/scss/TopCharts/Companies/GamesDetails/similarCompanies.scss';

const SimilarCompanies = () => {
  const { id } = useParams() as any;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const [paramsDefault, setParamsDefault] = useSetState<{
    [key in string]: any;
  }>({
    uuid: id,
    regionType: 'region',
    global: ['global'],
    listed: '',
    page_size: 10,
    page: 1,
    gameOwned: '',
    pcOrder: '',
    mobileOrder: '',
    consoleOrder: '',
    ratioOrder: '',
    totalsOrder: 'desc',
  });
  const { regionOptions } = useMarketFilterApi({});
  // const debouncedValue = useDebounce(paramsDefault, { wait: 800 });
  const { data, loading } = useSimilar(
    useCreation(
      () => ({
        ...paramsDefault,
      }),
      [paramsDefault],
    ),
  );
  const { data: revenueDataList } = useRevenueFilter(
    useCreation(
      () => ({
        type: [
          {
            tabName: 'mobile',
          },
        ],
        item: [
          {
            tabName: '',
          },
        ],
      }),
      [],
    ),
  );
  const popularList = useRef([]);

  const selectValue = useRef({
    markets: ['global'],
    regions: [],
    type: 'market',
  });

  const radioOptions = useRef([
    {
      value: 'global',
      text: 'Global',
      textCN: '全球',
    },
    {
      value: 'global_wo_china',
      text: 'Global except China mainland',
      textCN: '全球（不包括中国大陆）',
    },
  ]);

  const times = useCreation(() => {
    // 当前时间
    const now = moment(new Date());
    // 当前时间年份
    const initialYear = String(now.get('year'));
    // 当前时间月份 String
    const initialMonth = String(now.get('month') + 1)?.padStart(2, '0');
    // 当前时间日期 String
    const initialDate = String(now.get('date'))?.padStart(2, '0');
    // 当前日期 String YYYY-MM-DD
    const initialToday = `${initialYear}-${initialMonth}-${initialDate}`;
    return initialToday;
  }, []);

  const formatOptions = useMemo(
    () =>
      revenueDataList?.region_country?.map(
        (item: { country_set: any[]; subcontinent: string; continent: string }) => {
          const itemObjChildren = item?.country_set?.map((items) =>
            // 注意中英文和value的值（value有没有是英文名的特殊情况）
            ({
              ...items,
              text: items?.name,
              icon: getCountryFlag(items?.abbre),
              textCN: items?.name,
              value: items?.abbre,
            }),
          );
          return {
            ...item,
            country_set: itemObjChildren,
            children: itemObjChildren,
            text: item?.subcontinent,
            textCN: item?.subcontinent,
            value: item?.continent,
          };
        },
      ),
    [revenueDataList?.region_country],
  );

  const handleOnClickTableSort = useMemoizedFn((dataSource?: any) => {
    setParamsDefault((pre: any) => ({
      ...pre,
      ...dataSource,
      page: 1,
    }));
  });

  const handleOnChangePageIng = useMemoizedFn(
    ({ pageIndex: page, pageSize: page_size }: { pageIndex: number; pageSize: number }) => {
      setParamsDefault((pre) => ({
        ...pre,
        page,
        page_size,
      }));
    },
  );
  const handleonCloseReginSelect = useMemoizedFn((value) => {
    const { regions, markets, type: regionType } = value;
    if (_.isEmpty(regions)) return;
    const splitToupper = !_.isEmpty(regions) ? regions : markets;
    const global = _.split(_.toUpper(splitToupper), ',');
    setParamsDefault((pre) => ({
      ...pre,
      regionType,
      global,
    }));
  });

  return (
    <div className="dbi-similarcompanies">
      <div className="dbi-similarcompanies-head">
        <NewRegionSelect
          isTopCompanies
          popularList={popularList.current}
          locale={locale}
          value={selectValue.current}
          options={formatOptions}
          onCloseReginSelect={handleonCloseReginSelect}
          radioOptions={radioOptions.current}
          boxClassName="dbi-region-selector-dropdown"
        />
        <div className="dbi-similarcompanies-date">{times}</div>
      </div>
      <CardSlot>
        <CompaniesTable
          {...{
            className: 'dbi-companies-table',
            total: data?.total,
            pageIndex: paramsDefault?.page,
            pageSize: paramsDefault?.page_size,
            onPagingChange: handleOnChangePageIng,
            OnClickTableSort: handleOnClickTableSort,
            loading,
            tableData: useMemo(
              () =>
                smilarCompaniesTableData(
                  data?.similarCompany,
                  setParamsDefault,
                  formatMessage,
                  regionOptions,
                ),
              [data?.similarCompany, formatMessage, setParamsDefault, regionOptions],
            ),
          }}
        ></CompaniesTable>
      </CardSlot>
    </div>
  );
};

export default SimilarCompanies;
